"use client";

import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function CaseStudies() {
  const projects = [{
    title: "National SEO.",
    italicWord: "National",
    category: "Growth Strategy",
    year: "2024",
    wide: true,
    featured: true,
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/national_seo_growth.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/national_seo_growth.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/national_seo_growth.webp-wvc-sizes",
    alt: "National SEO strategy dashboard showing organic traffic growth"
  }, {
    title: "Ecommerce.",
    italicWord: "Ecommerce",
    category: "Revenue Scaling",
    year: "2024",
    wide: false,
    featured: false,
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/ecommerce_revenue_scaling.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/ecommerce_revenue_scaling.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/ecommerce_revenue_scaling.png-wvc-sizes",
    alt: "Ecommerce platform analytics showing increased conversion rates"
  }, {
    title: "Technical.",
    italicWord: "Technical",
    category: "Site Audit",
    year: "2023",
    wide: false,
    featured: false,
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/technical_seo_audit.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/technical_seo_audit.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/technical_seo_audit.png-wvc-sizes",
    alt: "Technical SEO audit report showing core web vitals improvements"
  }, {
    title: "International.",
    italicWord: "International",
    category: "Global Expansion",
    year: "2023",
    wide: true,
    featured: false,
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/international_seo_expansion.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/international_seo_expansion.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/international_seo_expansion.png-wvc-sizes",
    alt: "International SEO strategy map showing multi-region visibility"
  }, {
    title: "B2B Lead Gen.",
    italicWord: "B2B",
    category: "Lead Generation",
    year: "2023",
    wide: true,
    featured: false,
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/b2b_lead_generation.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/b2b_lead_generation.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/b2b_lead_generation.png-wvc-sizes",
    alt: "B2B lead generation funnel showing qualified inquiry growth"
  }, {
    title: "Local SEO.",
    italicWord: "Local",
    category: "Local Visibility",
    year: "2022",
    wide: false,
    featured: false,
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/local_seo_visibility.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/local_seo_visibility.png-wvc-sizes",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/local_seo_visibility.png-wvc-sizes",
    alt: "Local SEO map pack rankings showing top position results"
  }];
  const rows = [[projects[0], projects[1]], [projects[2], projects[3]], [projects[4], projects[5]]];
  const MarkerStroke = ({
    className = ""
  }: {
    className?: string;
  }) => <svg viewBox="0 0 220 22" className={className} aria-hidden="true" preserveAspectRatio="none">
      <path d="M4 14 C 40 6, 90 18, 130 10 S 200 8, 216 12 L 214 16 C 180 14, 120 20, 80 16 S 20 20, 6 18 Z" fill="var(--primary)" opacity="0.92" />
    </svg>;
  return <section data-section-id="30706" id="genuineimpactcasestudies" data-nav="light" className="bg-background text-foreground py-[clamp(96px,14vh,200px)]">
      <div className="mx-auto max-w-[1440px] px-[clamp(20px,4vw,80px)]">
        {}
        <div className="w-full h-px bg-border" />
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
          <div className="lg:col-span-3">
            <p className="font-sans text-[clamp(13px,0.92vw,15px)] font-medium uppercase tracking-[0.16em]" style={{
            color: "var(--muted-foreground)"
          }}>
              Verified Results
            </p>
          </div>
          <div className="lg:col-span-9">
            <h1 className="font-serif font-normal text-foreground" style={{
            fontSize: "clamp(45px, 7.2vw, 108px)",
            letterSpacing: "-0.025em",
            lineHeight: "1.0"
          }}>
              Impact I&rsquo;m{" "}
              <span className="relative inline-block italic">
                driven
                <MarkerStroke className="pointer-events-none absolute left-[-4%] right-[-4%] bottom-[-0.05em] w-[108%] h-[0.28em] -rotate-[3deg]" />
              </span>{" "}
              by.
            </h1>

            <div className="mt-10">
              <Button asChild variant="outline" className="group h-14 rounded-lg border-[1.5px] border-foreground bg-transparent px-8 text-[17px] font-medium tracking-[0.02em] text-foreground hover:bg-foreground hover:text-background transition-colors duration-300">
                <Link to="/CaseStudiesPage">
                  <span>See all case studies</span>
                  <ArrowRight className="ml-1 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {}
        <div className="mt-[clamp(64px,10vh,140px)] space-y-10 lg:space-y-16">
          {rows.map((row, rowIndex) => {
          const invert = rowIndex % 2 === 1;
          return <div key={rowIndex} data-index={rowIndex} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                {row.map((project, i) => {
              let span = "lg:col-span-7";
              if (!invert) {
                span = i === 0 ? "lg:col-span-7" : "lg:col-span-5";
              } else {
                span = i === 0 ? "lg:col-span-5" : "lg:col-span-7";
              }
              const isWide = project.wide;
              return <motion.div key={`${rowIndex}-${i}`} data-index={i} className={`${span} group relative`} whileHover={{
                y: -4
              }} transition={{
                duration: 0.38,
                ease: [0.22, 1, 0.36, 1]
              }}>
                      <div className="relative p-[clamp(20px,3vw,48px)]" style={{
                  backgroundColor: "var(--card)"
                }}>
                        {}
                        <div className="relative overflow-hidden rounded-3xl">
                          <div className={`relative w-full overflow-hidden ${isWide ? "aspect-[4/3] lg:aspect-[4/3]" : "aspect-[4/5] lg:aspect-[3/4]"}`}>
                            <img src={project.image} data-wvc-srcset={project.srcset} data-wvc-sizes={project.sizes} alt={project.alt} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[500ms] ease-out group-hover:scale-[1.03]" />
                          </div>
                        </div>

                        {}
                        <div className="mt-6 flex items-center gap-4">
                          <span className="inline-flex items-center rounded-lg border px-3.5 py-1.5 text-[14px] font-medium uppercase tracking-[0.14em] text-foreground transition-colors duration-300 group-hover:border-foreground" style={{
                      borderColor: "var(--border)"
                    }}>
                            {project.category}
                          </span>
                          <span className="font-sans text-[15px] font-medium tracking-[0.02em]" style={{
                      color: "var(--muted-foreground)"
                    }}>
                            {project.year}
                          </span>
                        </div>

                        {}
                        <h3 className="mt-5 font-serif font-normal text-foreground" style={{
                    fontSize: "clamp(30px, 3.6vw, 50px)",
                    letterSpacing: "-0.015em",
                    lineHeight: "1.1"
                  }}>
                          {project.title.split(project.italicWord).map((part, idx, arr) => <span key={idx}>
                              {part.replace(/\.$/, "")}
                              {idx < arr.length - 1 && <span className="italic">{project.italicWord}</span>}
                              {idx === arr.length - 1 && part.endsWith(".") && <span className="transition-colors duration-300 group-hover:text-primary" aria-hidden="true">
                                  .
                                </span>}
                            </span>)}
                        </h3>

                        {}
                        {project.featured && <motion.div className="absolute top-4 right-4 sm:top-6 sm:right-6 lg:top-8 lg:right-8 flex items-center justify-center rounded-lg shadow-md" style={{
                    backgroundColor: "var(--primary)",
                    color: "var(--primary-foreground)",
                    width: "clamp(56px, 6vw, 72px)",
                    height: "clamp(56px, 6vw, 72px)",
                    transform: "rotate(-8deg)"
                  }} whileHover={{
                    rotate: -2,
                    scale: 1.05
                  }} transition={{
                    duration: 0.32,
                    ease: [0.22, 1, 0.36, 1]
                  }} aria-label="Featured case study">
                            <span className="font-serif italic text-center leading-tight" style={{
                      fontSize: "clamp(13px, 1.1vw, 16px)"
                    }}>
                              Top
                            </span>
                          </motion.div>}
                      </div>
                    </motion.div>;
            })}
              </div>;
        })}
        </div>
      </div>
    </section>;
}